import React, { FunctionComponent } from 'react';

import BreadCrumbs from '@/components/atom/BreadCrumbs';
import ArrowsButtonDownMin from '@/components/atom/icon/ArrowsButtonDownMin';
import CategoryBottomSheet from '@/components/common/communication/qna/CategoryBottomSheet';
import QnaStickyWriteButton from '@/components/common/qna-sticky-write-button';
import TemplateLayout from '@/components/layouts/TemplateLayout';
import StickyAppHeader from '@/components/molecules/sticky-app-header';

import useQnaRealtimeViewModel from '@/hooks/qna/list/useQnaRealtimeViewModel';
import useScreenFocus from '@/hooks/useScreenFocus';
import { ClientType, CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';

import { DefaultButtonStyled } from '@/components/atom/buttons/DefaultButton';
import AppInstallTopBanner from '@/components/templates/mobile/communication/common/AppInstallTopBanner';
import RealtimeWebQna from '@/features/qna/realtime-web/ui/RealtimeWebQna';
import RealtimeWebviewQna from '@/features/qna/realtime-webview/ui/RealtimeWebviewQna';
import useAppInstallTopBanner from '@/hooks/cuj/useAppInstallTopBanner';
import { AdPageType } from '@/models/ad/list';
import { AdBandCta, QnaNativeDefault, UnifiedAdQnaNative } from '@/shared/ui';
import { UnifiedAdBanner } from '@/shared/ui';
import styled, { css } from 'styled-components';
import AiNotificationBanner from '../../common/board-template/qna-home/ai-notification-banner';

interface Props extends CustomPageProps {
  viewModel: ReturnType<typeof useQnaRealtimeViewModel>;
}
const QnaRealtimeTemplate: FunctionComponent<Props> = ({ client, viewModel }) => {
  useScreenFocus();

  const topBannerViewModel = useAppInstallTopBanner();

  return (
    <TemplateLayout.Wrapper>
      {client.clientType === ClientType.WEB && (
        <BreadCrumbsWrapper>
          <BreadCrumbs
            visible
            name={viewModel.selectedCategoriesBottomSheetTabItem?.title ?? '전체'}
            path={`${ROUTES.QNA.REALTIME.path}`}
            type={'qna'}
            urlParameter={
              viewModel.selectedCategoriesBottomSheetTabItem?.value
                ? `?categoryId=${viewModel.selectedCategoriesBottomSheetTabItem?.value}`
                : ''
            }
          />
        </BreadCrumbsWrapper>
      )}
      {client.isWebView && (
        <TemplateLayout.Header useSafeArea>
          <StickyAppHeader title={'실시간 의료상담'} />
        </TemplateLayout.Header>
      )}
      {client.clientType === ClientType.MOBILE_WEB && topBannerViewModel.isVisible && (
        <TemplateLayout.StickyHeader
          style={{
            top: 60,
          }}
        >
          <AdBandCta
            client={client}
            onClose={topBannerViewModel.hideBanner}
            enableAnimation={true}
            fallbackContent={
              <AppInstallTopBanner
                content='web_content_bnr_service'
                onClickClose={topBannerViewModel.hideBanner}
                trackingInfo={{
                  campaign: 'web_content_qna',
                }}
              />
            }
          />
        </TemplateLayout.StickyHeader>
      )}
      <TemplateLayout.Content>
        <CategoryWrapper>
          <h1>
            {client.clientType !== ClientType.MOBILE_WEBVIEW && '닥터나우 실시간 의료 상담 - '}
            {viewModel.selectedCategoriesBottomSheetTabItem?.title ?? '전체'}
          </h1>
          <CategoryButton onClick={viewModel.onClickCategoriesBottomSheetTrigger}>
            증상/과목 선택
            <ArrowsButtonDownMin size={12} />
          </CategoryButton>
        </CategoryWrapper>
        <UnifiedAdBanner
          className={'px-[24px] pt-[24px]'}
          adPageType={AdPageType.QnaListBanner}
          client={client}
          borderRadius={8}
          fallbackContent={null}
        />
        <AiNotificationBanner />
        {client.isWebView ? (
          <RealtimeWebviewQna
            onClickQuestion={viewModel.onClickQuestion}
            pageName={viewModel.pageName}
            advertisementComponent={
              <UnifiedAdQnaNative
                adPageType={AdPageType.QnaListNative}
                client={client}
                fallbackContent={
                  <QnaNativeDefault
                    title={'몸살감기 집에서 비대면 진료 가능할까요?'}
                    content={'기침이 심하고 열도 나서 외출이 어려운데, 비대면 진료로 약을 처방 받을 수 있을까요?'}
                    tagList={[{ name: '감기' }, { name: '코로나' }]}
                  />
                }
              />
            }
          />
        ) : (
          <RealtimeWebQna client={client} />
        )}
        <QnaStickyWriteButton
          buttonText={viewModel.bottomButtonText}
          pageName={viewModel.pageName}
          onClickButton={viewModel.onClickStickButton}
        />

        <CategoryBottomSheet
          isVisible={viewModel.showCategoriesBottomSheet}
          toggleBottomSheet={viewModel.toggleCategoriesBottomSheet}
          onClickCategoryItem={viewModel.onClickCategoriesBottomSheetTabListItem}
          categoryGroup={viewModel.categoryGroup}
          selectedTab={viewModel.selectedTab}
          onClickCategoriesBottomSheetTab={viewModel.onClickCategoriesBottomSheetTab}
          getSelectedTabItemList={viewModel.getSelectedTabItemList}
        />
      </TemplateLayout.Content>
    </TemplateLayout.Wrapper>
  );
};

export default QnaRealtimeTemplate;

const BreadCrumbsWrapper = styled.div`
  padding: 26px 24px 0 24px;
`;

const CategoryWrapper = styled.div`
  display: flex;
  ${({ theme }) =>
    theme.client.isMobile
      ? css`
          flex-direction: column;
          align-items: flex-start;
          gap: 8px;
        `
      : css`
          gap: 12px;
        `}
  padding: 16px 24px 0px;

  & > * {
    ${({ theme }) => `
    ${theme.typography.UI22SB};
    color: ${theme.color.gray900};
  `};
  }
`;

const CategoryButton = styled(DefaultButtonStyled)`
  padding: 6px 12px;
  border-radius: 8px;

  ${({ theme }) => `
    ${theme.typography.UI14M};
    color: ${theme.color.gray600};
    background: ${theme.color.gray50};
    
    &: hover {
      background: ${theme.color.gray100};
    }
    &: active {
      background: ${theme.color.gray200};
    }
  `};
`;
